import React, { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const SubServiceDetailNew2 = () => {
    const progressLineRef = useRef(null)
    const containerRef = useRef(null)
    const section2Ref = useRef(null)
    const bottle1Ref = useRef(null)
    const bottle2Ref = useRef(null)
    const bottle3Ref = useRef(null)
    const textRef = useRef(null)

    // Timeline points data
    const timelinePoints = [
        {
            id: 1,
            isActive: true,
            title: "It tells a story, ",
            subtitle: ["Your Story!"],
            position: "bottom"
        },
        {
            id: 2,
            isActive: false,
            title: "Builds confidence",
            subtitle: null,
            position: "top"
        },
        {
            id: 3,
            isActive: false,
            title: "Gives a purpose",
            subtitle: null,
            position: "bottom"
        },
        {
            id: 4,
            isActive: false,
            title: "& a direction",
            subtitle: null,
            position: "top"
        },
        {
            id: 5,
            isActive: false,
            title: "Attracts",
            subtitle: ["Ideal customers"],
            position: "bottom"
        }
    ]

    useEffect(() => {

        // Progress line animation
        gsap.set(progressLineRef.current, { scaleX: 0, transformOrigin: "left" })

        gsap.to(progressLineRef.current, {
            scaleX: 1,
            ease: "none", // Linear progression for smooth sync
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top 0",
                end: "+=200vh", // Requires 200vh of scrolling to complete
                scrub: 1.5, // Smoother scrubbing
                onUpdate: (self) => {
                    const progress = self.progress
                    const totalPoints = timelinePoints.length

                    // Calculate which points should be active based on line progress
                    // Each point activates when the line reaches its position (20%, 40%, 60%, 80%, 100%)
                    timelinePoints.forEach((point, index) => {
                        const pointElement = document.querySelector(`#point-${point.id}`)
                        if (pointElement) {
                            // Point threshold: each point activates at index/totalPoints progress
                            const pointThreshold = (index) / (totalPoints - 1)

                            if (progress >= pointThreshold) {
                                // Remove inactive classes for both light and dark modes
                                pointElement.classList.remove('bg-zinc-400', 'border-zinc-400', 'bg-zinc-700', 'border-zinc-700')
                                // Add active classes for both light and dark modes
                                pointElement.classList.add('bg-black', 'border-black', 'dark:bg-white', 'dark:border-white')
                                // Mark as active for theme-change reapplication
                                pointElement.dataset.active = 'true'
                                // Add glow effect when active
                                pointElement.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.6)'
                                // Dark mode glow
                                if (document.documentElement.classList.contains('dark')) {
                                    pointElement.style.boxShadow = '0 0 10px rgba(255, 255, 255, 0.6)'
                                }
                            } else {
                                // Remove active classes for both light and dark modes
                                pointElement.classList.remove('bg-black', 'border-black', 'dark:bg-white', 'dark:border-white')
                                // Add inactive classes for both light and dark modes
                                pointElement.classList.add('bg-zinc-400', 'border-zinc-400', 'dark:bg-zinc-700', 'dark:border-zinc-700')
                                // Mark as inactive
                                pointElement.dataset.active = 'false'
                                // Remove glow effect when inactive
                                pointElement.style.boxShadow = 'none'
                            }
                        }
                    })
                },
            }
        })

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill())
        }
    }, [])

    // React to theme changes in real-time to update active point glow/colors
    useEffect(() => {
        const root = document.documentElement

        // Function to reapply glow based on current theme for active points
        const reapplyActivePointStyles = () => {
            const isDark = root.classList.contains('dark')
            timelinePoints.forEach((point) => {
                const el = document.querySelector(`#point-${point.id}`)
                if (!el) return
                const isActive = el.dataset.active === 'true'
                if (isActive) {
                    el.style.boxShadow = isDark
                        ? '0 0 10px rgba(255, 255, 255, 0.6)'
                        : '0 0 10px rgba(0, 0, 0, 0.6)'
                }
            })
        }

        // Observer to detect theme toggles (class changes on <html>)
        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.type === 'attributes' && m.attributeName === 'class') {
                    reapplyActivePointStyles()
                }
            }
        })
        observer.observe(root, { attributes: true, attributeFilter: ['class'] })

        // Run once on mount in case of initial dark mode
        reapplyActivePointStyles()

        return () => observer.disconnect()
    }, [])

    // Render timeline point component
    const TimelinePoint = ({ point, index }) => {
        const isOdd = (point.id % 2) !== 0

        return (
            <div className="flex flex-col items-center relative" key={point.id}>
                <div
                    id={`point-${point.id}`}
                    data-active={index === 0 ? 'true' : 'false'}
                    className={`w-[15px] h-[15px] rounded-full border transition-colors -translate-x-[.1px] duration-300 ${index === 0 ? 'bg-black dark:bg-white border-black dark:border-white' : 'bg-zinc-400 dark:bg-zinc-700 border-zinc-400 dark:border-zinc-700'
                        }`}
                ></div>

                <span
                    className={`absolute xl:text-sm text-xs text-center w-36 ${isOdd ? 'top-8' : '-top-8'
                        } ${point.subtitle ? '' : ''}`}
                >
                    {point.title}
                    {point.subtitle && (
                        <>
                            <br />
                            {point.subtitle.map((line, i) => (
                                <span key={i} className={i === 1 ? 'font-medium' : ''}>
                                    {line}
                                    {i < point.subtitle.length - 1 && <br />}
                                </span>
                            ))}
                        </>
                    )}
                </span>
            </div>
        )
    }

    useEffect(() => {
        gsap.registerPlugin(ScrollTrigger)

        // Initialize bottle overlays to full height
        gsap.set([bottle1Ref.current, bottle2Ref.current, bottle3Ref.current], { height: '100%' })

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section2Ref.current,
                start: "top 0%",
                end: "top -190%",
                scrub: 1,
            }
        })

        // Animate each bottle overlay height to 0 (revealing colored background)
        tl.to(bottle1Ref.current, { height: '0%', duration: 1, ease: "power2.out" })
            .to(bottle2Ref.current, { height: '0%', duration: 1, ease: "power2.out" })
            .to(bottle3Ref.current, { height: '0%', duration: 1, ease: "power2.out" })

        const tl2 = gsap.timeline({
            scrollTrigger: {
                trigger: section2Ref.current,
                start: "top -190%",
                end: "top -250%",
                scrub: 1,
            }
        })

        // Animate each bottle overlay height to 0 (revealing colored background)
        tl2.to(textRef.current, { opacity: 1, duration: 1, ease: "power2.out" })

        return () => {
            ScrollTrigger.getAll().forEach(trigger => {
                if (trigger.trigger === section2Ref.current) {
                    trigger.kill()
                }
            })
        }
    }, []);
    return (
        <div className="text-black text-center xl:text-left dark:text-white bg-white dark:bg-zinc-800 w-screen relative " >

            <div className='absolute top-0 z-20 right-0 hidden xl:block'>
                <img src="/new-final-data/sections/12.webp" className='h-full -w-full' alt="" />
            </div>

            <div className='w-full relative z-30 py-20 xl:px-35 px-10 '>
                <div ref={containerRef} className='h-[150vh] relative'>
                    <div className='h-screen sticky top-0 w-full flex flex-col justify-center  '>
                        {/* Main Heading */}
                        <h1 className="xl:text-5xl text-4xl font-bold text-black dark:text-white mb-12 leading-tight">
                            Why Branding Matters?
                        </h1>

                        {/* Bullet Points */}
                        <div className="space-y-2 mb-26 max-w-4xl">
                            <div className="flex items-start xl:space-x-4">
                                <div className="w-2 h-2 bg-black dark:bg-white rounded-full hidden xl:block mt-3 flex-shrink-0"></div>
                                <p className="text-xl text-zinc-800 dark:text-zinc-300 leading-relaxed w-full ">
                                    Builds recognition through consistent visuals and messaging.
                                </p>
                            </div>
                            <div className="flex items-start xl:space-x-4">
                                <div className="w-2 h-2 bg-black dark:bg-white rounded-full hidden xl:block mt-3 flex-shrink-0"></div>
                                <p className="text-xl text-zinc-800 dark:text-zinc-300 leading-relaxed w-full ">
                                    Establishes credibility and professionalism.
                                </p>
                            </div>
                            <div className="flex items-start xl:space-x-4">
                                <div className="w-2 h-2 bg-black dark:bg-white rounded-full hidden xl:block mt-3 flex-shrink-0"></div>
                                <p className="text-xl text-zinc-800 dark:text-zinc-300 leading-relaxed w-full ">
                                    Influences customer decisions by creating an emotional bond.
                                </p>
                            </div>
                            <div className="flex items-start xl:space-x-4">
                                <div className="w-2 h-2 bg-black dark:bg-white rounded-full hidden xl:block mt-3 flex-shrink-0"></div>
                                <p className="text-xl text-zinc-800 dark:text-zinc-300 leading-relaxed w-full ">
                                    Adds long-term value to your business.
                                </p>
                            </div>
                        </div>
                        <div className="text-black dark:text-white overflow-hidden md:overflow-visible" >
                            {/* Timeline Container */}
                            <div className="relative flex items-center xl:w-3/5 w-[80%] py-12">
                                {/* Background Line */}
                                <div className="absolute top-1/2 left-0 w-full h-[2px] bg-zinc-400 dark:bg-zinc-600"></div>

                                {/* Animated Progress Line */}
                                <div
                                    ref={progressLineRef}
                                    className="absolute top-1/2 left-0 w-full h-[2px] bg-black dark:bg-white"
                                ></div>

                                {/* Points with Labels */}
                                <div className="flex justify-between w-full relative ">
                                    {timelinePoints.map((point, index) => (
                                        <TimelinePoint
                                            key={point.id}
                                            point={point}
                                            index={index}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div ref={section2Ref} className='w-full h-[350vh] relative'>
                    <div className='min-h-screen sticky top-0 w-full pt-24'>
                        <h1 className="xl:text-5xl text-4xl font-bold text-black dark:text-white mb-12  leading-tight">
                            Can you spot the difference <br className='hidden xl:block' /> between soda Brands!
                        </h1>

                        <div className='grid grid-cols-3 mt-20 xl:w-[60%] '>
                            <div className='relative h-[250px] w-[100px] flex items-center justify-center scale-[.8] xl:scale-100  '>
                                {/* SVG Definition - Adjustable height */}
                                <svg
                                    className=''
                                    width="100"
                                    height="250"
                                    viewBox="0 0 100 250"
                                    style={{ opacity: 0, pointerEvents: 'none' }}
                                >
                                    <defs>
                                        <mask id="customMask">
                                            <rect width="100%" height="100%" fill="black" />
                                            <image
                                                href="/new-final-data/sections/6.svg"
                                                width="100%"
                                                height="100%"
                                                fill="white"
                                            />
                                        </mask>
                                    </defs>
                                </svg>

                                <div className='w-15 absolute z-30 top-1/2 -translate-y-1/2 left-[50%] -translate-x-1/2'>
                                    <img src="/new-final-data/sections/pepsi.webp" alt="" />
                                </div>


                                {/* Red background with SVG mask - Separate height control */}
                                <div
                                    className='bg-[#0097F8] absolute top-0 right-0 z-20'
                                    style={{
                                        width: '100px',
                                        height: '400px',
                                        mask: 'url(#customMask)',
                                        WebkitMask: 'url(#customMask)'
                                    }}
                                >


                                </div>
                                <div
                                    ref={bottle1Ref}
                                    className='white-overlay bg-white dark:bg-zinc-50 absolute top-0 right-0 z-30'
                                    style={{
                                        width: '100px',
                                        height: '400px',
                                        mask: 'url(#customMask)',
                                        WebkitMask: 'url(#customMask)'
                                    }}
                                >


                                </div>
                            </div>
                            <div className='relative h-[250px] w-[100px] flex items-center justify-center scale-[.8] xl:scale-100  '>
                                {/* SVG Definition - Adjustable height */}
                                <svg
                                    className=''
                                    width="100"
                                    height="250"
                                    viewBox="0 0 400 250"
                                    style={{ opacity: 0, pointerEvents: 'none' }}
                                >
                                    <defs>
                                        <mask id="customMask">
                                            <rect width="100%" height="100%" fill="black" />
                                            <image
                                                href="/new-final-data/sections/6.svg"
                                                width="100%"
                                                height="100%"
                                                fill="white"
                                            />
                                        </mask>
                                    </defs>
                                </svg>

                                <div className='w-24 absolute z-30 top-1/2 -translate-y-1/2 left-[50%] -translate-x-1/2'>
                                    <img src="/new-final-data/sections/coke.webp" alt="" />
                                </div>
                                {/* Red background with SVG mask - Separate height control */}
                                <div
                                    className='bg-[#F50101] absolute top-0 right-0 z-20'
                                    style={{
                                        width: '100px',
                                        height: '400px',
                                        mask: 'url(#customMask)',
                                        WebkitMask: 'url(#customMask)'
                                    }}
                                ></div>
                                <div
                                    ref={bottle2Ref}
                                    className='white-overlay bg-white dark:bg-zinc-50 absolute top-0 right-0 z-30'
                                    style={{
                                        width: '100px',
                                        height: '400px',
                                        mask: 'url(#customMask)',
                                        WebkitMask: 'url(#customMask)'
                                    }}
                                ></div>
                            </div>
                            <div className='relative h-[250px] w-[100px] flex items-center justify-center scale-[.8] xl:scale-100   '>
                                {/* SVG Definition - Adjustable height */}
                                <svg
                                    className=''
                                    width="100"
                                    height="250"
                                    viewBox="0 0 400 250"
                                    style={{ opacity: 0, pointerEvents: 'none' }}
                                >
                                    <defs>
                                        <mask id="customMask">
                                            <rect width="100%" height="100%" fill="black" />
                                            <image
                                                href="/new-final-data/sections/6.svg"
                                                width="100%"
                                                height="100%"
                                                fill="white"
                                            />
                                        </mask>
                                    </defs>
                                </svg>

                                <div className='w-20 absolute z-30 top-1/2 -translate-y-1/2 left-[50%] -translate-x-1/2'>
                                    <img src="/new-final-data/sections/thumbsup.webp" alt="" />
                                </div>
                                {/* Red background with SVG mask - Separate height control */}
                                <div
                                    className='bg-[#1A4183] absolute top-0 right-0 z-20'
                                    style={{
                                        width: '100px',
                                        height: '400px',
                                        mask: 'url(#customMask)',
                                        WebkitMask: 'url(#customMask)'
                                    }}
                                ></div>
                                <div
                                    ref={bottle3Ref}
                                    className='white-overlay bg-white dark:bg-zinc-50 absolute top-0 right-0 z-30'
                                    style={{
                                        width: '100px',
                                        height: '400px',
                                        mask: 'url(#customMask)',
                                        WebkitMask: 'url(#customMask)'
                                    }}
                                ></div>
                            </div>
                        </div>


                        <div ref={textRef} className='opacity-0 mt-20'>
                            <h1 className="xl:text-5xl text-4xl font-bold text-black dark:text-white mb-6 leading-tight">
                                Well, Now you CAN!!
                            </h1>

                            <p className="text-xl font-[100] text-zinc-800 dark:text-zinc-300 mb-8 max-w-3xl w-full leading-relaxed">
                                There are many identical products that flood the market, but brands are always unique. For example, Coke, Himalayan Products, products of Amul, there are similar, but not identical products of these ‘brands.’
                            </p>
                            <h1 className="xl:text-5xl text-4xl font-bold text-black dark:text-white mb-6 leading-tight mt-30">
                                How We Do It Differently?
                            </h1>

                            <p className="text-xl font-[100] text-zinc-800 dark:text-zinc-300 mb-8 max-w-3xl w-full leading-relaxed">
                                We blend creativity with data-driven insights, backed by market research and competitor analysis. Every design is tailor-made-never from templates-and shaped through collaborative workshops to keep your brand authentic and unique.
                            </p>
                        </div>

                    </div>

                </div>
            </div>
        </div>
    )
}

export default SubServiceDetailNew2